import { API_ENDPOINTS } from '../config/api.config';
import httpService from './http.service';

class ProjectService {
  // Récupérer la liste des projets
  async getProjects(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const endpoint = `${API_ENDPOINTS.PROJECTS.LIST}${queryParams ? `?${queryParams}` : ''}`;
      
      const response = await httpService.get(endpoint);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des projets:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Récupérer un projet par ID
  async getProjectById(projectId) {
    try {
      const response = await httpService.get(API_ENDPOINTS.PROJECTS.DETAIL(projectId));
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error(`Erreur lors de la récupération du projet ${projectId}:`, error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Créer un nouveau projet
  async createProject(projectData) {
    try {
      const response = await httpService.post(API_ENDPOINTS.PROJECTS.CREATE, projectData);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error('Erreur lors de la création du projet:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Mettre à jour un projet
  async updateProject(projectId, projectData) {
    try {
      const response = await httpService.put(API_ENDPOINTS.PROJECTS.UPDATE(projectId), projectData);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error(`Erreur lors de la mise à jour du projet ${projectId}:`, error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Supprimer un projet
  async deleteProject(projectId) {
    try {
      const response = await httpService.delete(API_ENDPOINTS.PROJECTS.DELETE(projectId));
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error(`Erreur lors de la suppression du projet ${projectId}:`, error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Récupérer les étapes d'un projet
  async getProjectSteps(projectId) {
    try {
      console.log(`Récupération des étapes pour le projet ${projectId}...`);
      
      // Utiliser l'endpoint spécifique pour récupérer les étapes du projet
      const stepsResponse = await httpService.get(API_ENDPOINTS.STEPS.LIST_BY_PROJECT(projectId));
      console.log('Étapes récupérées pour le projet:', stepsResponse);
      
      let projectSteps = stepsResponse;
      
      // Si l'API retourne un objet avec une propriété results
      if (stepsResponse && stepsResponse.results && Array.isArray(stepsResponse.results)) {
        projectSteps = stepsResponse.results;
      }
      
      console.log(`Étapes du projet ${projectId}:`, projectSteps);
      
      return {
        success: true,
        data: projectSteps,
      };
    } catch (error) {
      console.error(`Erreur lors de la récupération des étapes du projet ${projectId}:`, error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Récupérer une étape spécifique
  async getProjectStep(stepId) {
    try {
      const response = await httpService.get(API_ENDPOINTS.STEPS.DETAIL(stepId));
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'étape ${stepId}:`, error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Récupérer les sections d'une étape
  async getStepSections(stepId) {
    try {
      console.log(`Récupération des sections pour l'étape ${stepId}...`);
      
      // D'abord, essayer de récupérer toutes les sections
      const allSectionsResponse = await httpService.get(API_ENDPOINTS.SECTIONS.LIST());
      console.log('Toutes les sections récupérées:', allSectionsResponse);
      
      if (allSectionsResponse && Array.isArray(allSectionsResponse)) {
        // Filtrer les sections par étape
        const sectionsForStep = allSectionsResponse.filter(section => 
          section.step === stepId || section.step_id === stepId
        );
        
        console.log(`Sections filtrées pour l'étape ${stepId}:`, sectionsForStep);
        
        return {
          success: true,
          data: sectionsForStep,
        };
      } else if (allSectionsResponse && allSectionsResponse.results && Array.isArray(allSectionsResponse.results)) {
        // Si l'API retourne un objet avec une propriété results
        const sectionsForStep = allSectionsResponse.results.filter(section => 
          section.step === stepId || section.step_id === stepId
        );
        
        console.log(`Sections filtrées pour l'étape ${stepId} (depuis .results):`, sectionsForStep);
        
        return {
          success: true,
          data: sectionsForStep,
        };
      }
      
      return {
        success: true,
        data: [],
      };
    } catch (error) {
      console.error(`Erreur lors de la récupération des sections de l'étape ${stepId}:`, error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Récupérer une section spécifique
  async getStepSection(stepId, sectionId) {
    try {
      const response = await httpService.get(API_ENDPOINTS.SECTIONS.DETAIL(sectionId));
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error(`Erreur lors de la récupération de la section ${sectionId}:`, error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Récupérer les contenus d'une section
  async getSectionContents(sectionId) {
    try {
      // Essayer d'abord de récupérer les contenus spécifiques à la section
      const response = await httpService.get(API_ENDPOINTS.CONTENTS.LIST_BY_SECTION(sectionId));
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error(`Erreur lors de la récupération des contenus de la section ${sectionId}:`, error);
      // Fallback : récupérer tous les contenus
      try {
        console.log('Tentative de récupération de tous les contenus comme fallback...');
        const fallbackResponse = await httpService.get(API_ENDPOINTS.CONTENTS.LIST());
        return {
          success: true,
          data: fallbackResponse,
          warning: 'Utilisation de tous les contenus disponibles (fallback)'
        };
      } catch (fallbackError) {
        console.error('Erreur lors de la récupération des contenus (fallback):', fallbackError);
        return {
          success: false,
          error: error.message,
        };
      }
    }
  }

  // Récupérer un contenu spécifique
  async getSectionContent(contentId) {
    try {
      const response = await httpService.get(API_ENDPOINTS.CONTENTS.DETAIL(contentId));
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error(`Erreur lors de la récupération du contenu ${contentId}:`, error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Soumettre un contenu
  async submitContent(contentId, contentData) {
    try {
      const response = await httpService.post(API_ENDPOINTS.CONTENTS.SUBMIT(contentId), contentData);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error(`Erreur lors de la soumission du contenu ${contentId}:`, error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Récupérer un produit
  async getProduct(productId) {
    try {
      const response = await httpService.get(API_ENDPOINTS.PRODUCTS.DETAIL(productId));
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error(`Erreur lors de la récupération du produit ${productId}:`, error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Récupérer un input
  async getInput(inputId) {
    try {
      const response = await httpService.get(API_ENDPOINTS.INPUTS.DETAIL(inputId));
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'input ${inputId}:`, error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Récupérer un autre choix
  async getOtherChoice(choiceId) {
    try {
      const response = await httpService.get(API_ENDPOINTS.OTHER_CHOICES.DETAIL(choiceId));
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      console.error(`Erreur lors de la récupération du choix ${choiceId}:`, error);
      return {
        success: false,
        error: error.message,
      };
    }
  }
}

// Instance singleton
export const projectService = new ProjectService();
export default projectService; 